import React, { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import toast from "react-hot-toast";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { ArrowLeft, ShoppingCart, User, Edit2, Search, UserPlus } from "lucide-react";
import { Badge } from "../../ui/badge";
import SharedDataContext from "../../../SharedDataContext";
import Image from "../../Tags/Image";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import {
  getMembers,
  getAllPhoneNumbers,
  getAccessiblePhoneNumbers,
  setupDialerAccess,
  purchasePhoneNumber,
  getTelephonyUsersSimple,
} from "../../../apis/phoneInventory";
import { usePhoneInventoryStore } from "../../../stores/usePhoneInventoryStore";
import PurchaseNumberPanel from "../PhoneInventory/PurchaseNumberPanel";
import PageHeader from "../../PageHeader";
import { getCountryFlag } from "../../../constants/countries";
import Flag from "../../Flag";

// Helper function to render capabilities
const renderCapabilities = (capabilities) => {
  if (!capabilities || !Array.isArray(capabilities)) {
    return null;
  }

  return capabilities.map((capability, index) => (
    <span
      key={index}
      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
    >
      {capability}
    </span>
  ));
};

// Step SVG Components
const ActiveStepSvg = ({ isCompleted }) => (
  <svg width="25" height="24" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 0 C8.04071716 5.46768767 14.35713546 11.78672485 16.953125 21.328125 C17.97008074 32.73931439 17.08780568 41.52074309 9.640625 50.578125 C3.75028057 56.89640044 -1.9995096 60.87830798 -10.81640625 61.6640625 C-22.23808647 61.92046185 -29.76998744 60.10045244 -38.2578125 52.1875 C-45.41707366 44.7468705 -47.46537447 37.63799487 -47.41040039 27.37304688 C-47.154444 17.46241552 -43.06295379 11.07888989 -36.046875 4.328125 C-26.55128048 -3.80505814 -11.27874483 -6.1129075 0 0 Z"
      className="fill-[#8058D9]"
      transform="translate(47.046875,2.671875)"
    />
    <path
      d="M0 0 C8.04071716 5.46768767 14.35713546 11.78672485 16.953125 21.328125 C17.97008074 32.73931439 17.08780568 41.52074309 9.640625 50.578125 C3.75028057 56.89640044 -1.9995096 60.87830798 -10.81640625 61.6640625 C-22.23808647 61.92046185 -29.76998744 60.10045244 -38.2578125 52.1875 C-45.41707366 44.7468705 -47.46537447 37.63799487 -47.41040039 27.37304688 C-47.154444 17.46241552 -43.06295379 11.07888989 -36.046875 4.328125 C-26.55128048 -3.80505814 -11.27874483 -6.1129075 0 0 Z M-35.3828125 16.01171875 C-39.69498372 22.01434207 -39.91523726 28.15271054 -39.046875 35.328125 C-36.7704698 42.19969234 -31.95580205 47.28811995 -26.046875 51.328125 C-19.95513856 54.29436268 -12.95319606 54.62220671 -6.51171875 52.48828125 C0.05359274 49.14368861 5.04222437 44.06684362 8.390625 37.515625 C9.63144691 30.48430085 10.10619438 23.59603687 6.4140625 17.24609375 C1.87004252 11.28054881 -3.44457613 6.39511432 -11.046875 5.328125 C-22.26366598 4.1843075 -28.10245461 7.6726634 -35.3828125 16.01171875 Z"
      className="fill-[#A07AED]"
      transform="translate(47.046875,2.671875)"
    />
    <path
      d="M0 0 C6.37941264 4.9925838 10.29700078 10.20220399 11.875 18.25 C12.91383772 27.54182628 10.61126309 34.87480459 4.875 42.25 C-2.26963789 48.92868324 -9.08151085 51.64490376 -18.734375 51.453125 C-27.4121896 50.71579435 -33.39207027 46.68374879 -39.125 40.25 C-44.05527458 32.86137914 -45.23317475 24.67741353 -43.734375 15.984375 C-41.7688851 8.77757868 -37.20664105 2.9292689 -30.96484375 -1.16796875 C-20.32951343 -6.67448557 -10.26158424 -5.74648718 0 0 Z M-36.4609375 9.93359375 C-40.77310872 15.93621707 -40.99336226 22.07458554 -40.125 29.25 C-37.8485948 36.12156734 -33.03392705 41.20999495 -27.125 45.25 C-21.03326356 48.21623768 -14.03132106 48.54408171 -7.58984375 46.41015625 C-1.02453226 43.06556361 3.96409937 37.98871862 7.3125 31.4375 C8.55332191 24.40617585 9.02806938 17.51791187 5.3359375 11.16796875 C0.79191752 5.20242381 -4.52270113 0.31698932 -12.125 -0.75 C-23.34179098 -1.8938175 -29.18057961 1.5945384 -36.4609375 9.93359375 Z"
      className="fill-[#F8F5FD]"
      transform="translate(48.125,8.75)"
    />

    {/* Center dot for current step or checkmark for completed step */}
    {!isCompleted ? (
      <path
        d="M0 0 C4.0743356 0.84881992 5.54405875 2.30225596 8 5.625 C8.54667869 8.97340698 8.34038506 10.83785954 7 13.9375 C3.96275614 16.5001745 0.90933118 16.79839775 -3 16.625 C-5.4375 15.6875 -5.4375 15.6875 -7 13.625 C-7.95798882 10.88704431 -8.32609912 8.51491291 -8 5.625 C-5.60540961 2.14195943 -4.14822958 0.8642145 0 0 Z"
        className="fill-[#FAF8FD]"
        transform="translate(32,23.375)"
      />
    ) : (
      <path
        d="M22 32L30 40L42 28"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const InactiveStepSvg = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outer circle */}
    <circle cx="16" cy="16" r="15" className="fill-[#FAFAFA]" />

    {/* Border */}
    <circle cx="16" cy="16" r="14.5" className="stroke-[#E9EAEB]" strokeWidth="1" />

    {/* Center dot */}
    <circle cx="16" cy="16" r="4" className="fill-[#D5D7DA]" />
  </svg>
);

export default function AddDialerUserContainer() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();
  const captureEvent = useCaptureEvent();

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [selectedNumberData, setSelectedNumberData] = useState(null); // Store full number data

  // New state for redesigned flow
  const [currentStep, setCurrentStep] = useState(1); // 1: Select User, 2: Purchase/Assign Number
  const [searchQuery, setSearchQuery] = useState(""); // For user search
  const [filteredMembers, setFilteredMembers] = useState([]); // Filtered user results

  // API state
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const isAdmin = activeWorkspace?.role === "Owner" || activeWorkspace?.role === "Admin";

  // Purchase number panel state
  const { openPurchaseNumberPanel } = usePhoneInventoryStore();

  // 🎯 Enhanced purchase handler with full context
  const handleOpenPurchasePanel = () => {
    const selectedUserData = teamMembers.find((member) => member.id === selectedUser);
    captureEvent("SET_DialerAI_AddUser_PurchasePanelOpen", {
      user_id: selectedUserData?.id,
    });

    openPurchaseNumberPanel("add-user", {
      selectedUser: selectedUserData,
      returnTo: "add-user",
      workspaceSlug: workspaceSlug,
      sourceForm: "dialer-user-setup",
    });
  };

  // 📡 Listen for number selection
  useEffect(() => {
    const handleNumberSelected = (event) => {
      const { number, user } = event.detail;

      // Store both the ID and full number data
      setSelectedNumber(number.id || number.phone_number);
      setSelectedNumberData(number); // Store full number data for preview
      toast.success(`Number ${number?.phone_number ? number.phone_number : "number"} selected for ${user?.name || "the user"}!`);
      captureEvent("SET_DialerAI_AddUser_NumberSelected", {
        number_id: number?.id,
        user_id: user?.id,
      });
    };

    const handleInventoryUpdate = (event) => {
      const { number } = event.detail;

      // For inventory context, just show success
      toast.success(`Number ${number.phone_number} added to inventory!`);
    };

    window.addEventListener("numberSelectedForUser", handleNumberSelected);
    window.addEventListener("numberAddedToInventory", handleInventoryUpdate);

    return () => {
      window.removeEventListener("numberSelectedForUser", handleNumberSelected);
      window.removeEventListener("numberAddedToInventory", handleInventoryUpdate);
    };
  }, []);

  // Fetch team members
  const getMembersFn = useQuery(
    ["get_team_members_dropdown"],
    () => {
      return getMembers({
        query: "per_page=100", // Get more members for dropdown
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      });
    },
    {
      enabled: !!activeWorkspace?.id,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const membersArray = data?.data || [];
        const mappedMembers = membersArray.map((member) => ({
          id: member.id,
          name: member.name,
          email: member.email,
        }));
        setTeamMembers(mappedMembers);
        setFilteredMembers(mappedMembers); // Initialize filtered members
      },
      onError: (error) => {
        console.error("Error fetching members:", error);
        // Fallback to mock data on error
      },
    }
  );

  const { data: telephonyUsersData } = useQuery(
    ["get_dialer_access_users", activeWorkspace?.id],
    () =>
      getTelephonyUsersSimple({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: !!activeWorkspace?.id,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error("Error fetching users with dialer access:", error);
      },
    }
  );

  const availableTeamMembers = useMemo(() => {
    const telephonyUsers = telephonyUsersData?.data || [];
    const usersWithAccess = new Set(telephonyUsers.map((user) => user?.user_id || user?.id).filter(Boolean));

    return teamMembers.filter((member) => member?.id && !usersWithAccess.has(member.id));
  }, [teamMembers, telephonyUsersData]);

  useEffect(() => {
    if (selectedUser && !availableTeamMembers.some((member) => member.id === selectedUser)) {
      setSelectedUser("");
      setSelectedNumber("");
      setSelectedNumberData(null);
      setCurrentStep(1);
    }
  }, [availableTeamMembers, selectedUser]);

  // Fetch available phone numbers
  const getPhoneNumbersFn = useQuery(
    ["get_phone_numbers_dropdown"],
    () => {
      const fetchFn = isAdmin ? getAllPhoneNumbers : getAccessiblePhoneNumbers;
      return fetchFn({
        query: "per_page=100", // Get more numbers for dropdown
        options: {
          headers: {
            "X-Workspace-ID": activeWorkspace?.id,
          },
        },
      });
    },
    {
      enabled: !!activeWorkspace?.id,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const numbersData = data?.data || [];

        // 🚫 Remove duplicates by phone number
        const uniqueNumbers = numbersData.filter(
          (number, index, array) => array.findIndex((n) => n.phone_number === number.phone_number) === index
        );

        setAvailableNumbers(
          uniqueNumbers.map((number) => {
            const location =
              [number.locality, number.region, number.country].filter(Boolean).join(", ") ||
              "Unknown Location";

            return {
              id: number.id,
              number: number.phone_number,
              location: location,
              label: number.label,
              type: number.type,
              fullData: number,
              countryCode: number.country,
            };
          })
        );
      },
      onError: (error) => {
        console.error("Error fetching phone numbers:", error);
        // Fallback to mock data on error
        setAvailableNumbers([
          { id: "num1", number: "+1 (555) 123-4567", location: "New York, NY" },
          { id: "num2", number: "+1 (555) 987-6543", location: "Los Angeles, CA" },
          { id: "num3", number: "+1 (555) 456-7890", location: "Chicago, IL" },
        ]);
      },
    }
  );

  // Setup dialer access mutation
  const setupDialerMutation = useMutation(
    ({ users }) => {
      return setupDialerAccess({
        users,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
    },
    {
      onSuccess: (data) => {
        toast.success("Dialer access has been successfully set up for the user!");
        // Navigate back to Dialer AI page
        navigate(`/${workspaceSlug}/dialer-ai`);
        captureEvent("SET_DialerAI_AddUser_SubmitSuccess", {
          users_count: Array.isArray(data?.users) ? data.users.length : 1,
        });
        // If a purchase just occurred, emit purchase success with the created number id if present
        const firstUser = Array.isArray(data?.users) ? data.users[0] : undefined;
        const purchasedNumberId =
          firstUser && Array.isArray(firstUser.phone_number_ids) ? firstUser.phone_number_ids[0] : undefined;
        if (purchasedNumberId) {
          captureEvent("SET_DialerAI_AddUser_PurchaseSuccess", {
            number_id: purchasedNumberId,
          });
        }
      },
      onError: (error) => {
        console.error("Error setting up dialer access:", error);
        const errorMessage =
          error?.data?.message || error?.message || "Failed to setup dialer access. Please try again.";
        // toast.error(errorMessage);
        captureEvent("SET_DialerAI_AddUser_SubmitFailure", {
          error: errorMessage,
        });
      },
    }
  );

  const handleCancel = () => {
    navigate(`/${workspaceSlug}/dialer-ai`);
  };

  // Helper functions for new flow
  const handleUserSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredMembers(teamMembers);
      return;
    }

    const filtered = teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(query.toLowerCase()) ||
        member.email.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredMembers(filtered);
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setCurrentStep(2); // Move to step 2
    captureEvent("SET_DialerAI_AddUser_SelectUser", {
      user_id: userId,
    });
  };

  const handleMemberClick = (member, { forceLog = false } = {}) => {
    if (forceLog || selectedUser !== member.id) {
      console.log("member", member);
    }
    setCurrentStep(2);
  };

  const handleEditUser = () => {
    setCurrentStep(1); // Go back to step 1
  };

  const getSelectedUserData = () => {
    return teamMembers.find((member) => member.id === selectedUser);
  };

  const [purchaseLoading, setPurchaseLoading] = useState(false);

  useEffect(() => {
    if (activeWorkspace?.id) {
      captureEvent("SET_DialerAI_AddUserView", {
        workspace_id: activeWorkspace.id,
      });
    }
  }, [activeWorkspace?.id, captureEvent]);

  const handleSaveAndContinue = async () => {
    // Validate required fields
    if (!selectedUser || selectedUser === "loading" || selectedUser === "no-members") {
      toast.error("Please select a team member first.");
      return;
    }

    // Check if we have either a selected number from dropdown OR from purchase panel
    const hasSelectedNumber =
      (selectedNumber && selectedNumber !== "loading" && selectedNumber !== "no-numbers") ||
      selectedNumberData;

    if (!hasSelectedNumber) {
      toast.error("Please select a phone number to assign.");
      return;
    }

    // Prepare API payload - use the correct phone number ID
    const phoneNumberId = selectedNumberData ? selectedNumberData.id : selectedNumber;

    // Transform data to match new API format
    const usersPayload = [
      {
        user_id: selectedUser,
        phone_number_ids: [phoneNumberId],
        recording_enabled: true,
      },
    ];

    captureEvent("SET_DialerAI_AddUser_SubmitClick", {
      user_id: selectedUser,
      phone_number_id: selectedNumberData ? selectedNumberData.id : selectedNumber,
      purchased_number: !!selectedNumberData,
    });

    if (selectedNumberData && selectedNumberData.phone_number) {
      setPurchaseLoading(true);
      const purchaseRequestData = {
        phone_number: selectedNumberData.phone_number,
        label: selectedNumberData.phone_number,
        type: "Assigned",
        // assign_to_users: [selectedUser],
      };

      // purchaseRequestData.phone_number = "+18158843168";
      // purchaseRequestData.label = "+18158843168";

      console.log("purchaseRequestData", purchaseRequestData);

      const response = await purchasePhoneNumber({
        data: purchaseRequestData,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
      console.log("response", response);
      setPurchaseLoading(false);
      if (response && response.data && response.data?.id) {
        usersPayload[0].phone_number_ids = [response?.data?.id];
        console.log("usersPayload in submit after", usersPayload[0].phone_number_ids);
        setupDialerMutation.mutate({
          users: usersPayload,
        });
      }
    } else {
      // Call the API with new format
      setupDialerMutation.mutate({
        users: usersPayload,
      });
    }
  };

  console.log("selectedNumberData", { selectedNumberData, selectedNumber, availableNumbers });

  return (
    <div className="w-full mx-auto">
      {/* Main Card Container */}
      <div>
        <PageHeader
          heading={
            <>
              <span
                onClick={handleCancel}
                className="cursor-pointer inline-flex items-center mb-2 text-gray-600 font-semibold text-sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Cancel and go back
              </span>
              <div>Add user access</div>
            </>
          }
          description="Select users in your workspace to give access to dialer"
          cta={
            <>
              <Button
                onClick={handleSaveAndContinue}
                disabled={
                  currentStep === 1 ||
                  !selectedUser ||
                  setupDialerMutation.isLoading ||
                  purchaseLoading ||
                  (currentStep === 2 && !selectedNumber && !selectedNumberData)
                }
                className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6941C6] text-[#FFFFFF] font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {setupDialerMutation.isLoading ? "Setting up..." : "Confirm"}{" "}
                {setupDialerMutation.isLoading ||
                  (purchaseLoading && <div className="ml-2 loader_circle"></div>)}
              </Button>
            </>
          }
        />

        {/* Main Content Area */}
        <div className="flex gap-8">
          {/* Left Side - Progress Stepper */}
          <div className="w-80 flex-shrink-0 relative">
            {/* Step 1 - Add User (positioned to align with card center) */}
            <div className="absolute" style={{ top: "20px" }}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-4 flex items-center justify-center">
                    {currentStep >= 1 ? <ActiveStepSvg isCompleted={currentStep > 1} /> : <InactiveStepSvg />}
                  </div>
                  {/* Connecting line */}
                  <div
                    className={`w-[2px] ${currentStep >= 2 ? "bg-violet-600" : "bg-gray-200"}`}
                    style={{ height: "360px", marginLeft: "15px" }}
                  ></div>
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-base font-semibold ${currentStep >= 1 ? "text-[#6941C6]" : "text-gray-900"}`}
                  >
                    Select user
                  </h3>
                  <p className={`text-xs mt-1 ${currentStep >= 1 ? "text-[#7F56D9]" : "text-gray-500"}`}>
                    Select user from {process.env.REACT_APP_APP_NAME} to provide them dialer access.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 - Purchase Number (positioned to align with card center) */}
            <div className="absolute" style={{ top: "390px" }}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 flex items-center justify-center">
                    {currentStep >= 2 ? <ActiveStepSvg isCompleted={false} /> : <InactiveStepSvg />}
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-base font-semibold ${currentStep >= 2 ? "text-[#6941C6]" : "text-gray-400"}`}
                  >
                    Purchase or assign number
                  </h3>
                  <p className={`text-xs mt-1 ${currentStep >= 2 ? "text-[#7F56D9]" : "text-gray-400"}`}>
                    Purchase a phone number for the user to make calls from or assign an existing number.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content Area */}
          <div className="flex-1">
            {/* Step 1: Select User */}
            {currentStep === 1 && (
              <div className=" p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Select user</h2>
                    <p className="text-sm text-gray-500 mt-1 border-b border-border pb-4">
                      Select user from {process.env.REACT_APP_APP_NAME} to grant dialer access. If the user
                      isn't part of {process.env.REACT_APP_APP_NAME} yet, you can invite them here.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <Select
                    value={selectedUser}
                    onValueChange={(value) => {
                      // Prevent selection of placeholder values
                      if (value !== "loading" && value !== "no-members") {
                        handleUserSelect(value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full h-12 border-gray-300 ">
                      <div className="inline-flex items-center space-x-2.5">
                        <User className="h-4 w-4 text-gray-500" />
                        <SelectValue
                          placeholder={getMembersFn.isLoading ? "Loading members..." : "Select team member"}
                        />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {getMembersFn.isLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading members...
                        </SelectItem>
                      ) : teamMembers.length === 0 ? (
                        <SelectItem value="no-members" disabled>
                          No team members found
                        </SelectItem>
                      ) : availableTeamMembers.length === 0 ? (
                        <SelectItem value="no-eligible-members" disabled>
                          All team members already have dialer access
                        </SelectItem>
                      ) : (
                        availableTeamMembers.map((member) => (
                          <SelectItem
                            key={member.id}
                            value={member.id}
                            onPointerUp={() => {
                              if (selectedUser === member.id && availableTeamMembers.length === 1) {
                                handleMemberClick(member, { forceLog: true });
                              }
                            }}
                          >
                            <p className="font-medium text-left">{member.name}</p>
                            <p className="text-xs text-gray-500 text-left">{member.email}</p>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 2: Purchase or Assign Number */}
            {currentStep === 2 && (
              <div className="p-6">
                {/* Selected User Display - Only show if no number selected from purchase panel */}
                {selectedUser && !selectedNumberData && (
                  <div className="mb-6 p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {getSelectedUserData()?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-950">{getSelectedUserData()?.name}</p>
                          <p className="text-xs text-gray-600">{getSelectedUserData()?.email}</p>
                        </div>
                      </div>
                      <Button variant="purple_outline" size="sm" onClick={handleEditUser}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Header - Only show if no number selected from purchase panel */}
                {!selectedNumberData && (
                  <div className="flex items-center space-x-3 mb-6 border-b border-border pb-4">
                    <div>
                      <h2 className="text-base font-semibold text-gray-950">Purchase or assign number</h2>
                      <p className="text-sm mt-1 text-gray-600">
                        Purchase a phone number for the user to make calls from or assign an existing number
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Show simplified view when number is selected from purchase panel */}
                  {selectedNumberData ? (
                    <>
                      {/* Number Preview Card */}
                      <div className="bg-background border border-gray-200 rounded-lg p-5">
                        <div className="mb-4">
                          <h2 className="text-base font-bold text-gray-900">Number Preview</h2>
                          <p className="text-sm text-gray-500 mt-1">
                            Selected phone number that will be assigned to the user
                          </p>
                        </div>

                        {/* Selected Number Display */}
                        <div className="p-4 border-t border-border">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 rounded-full flex items-start leading-none justify-center overflow-hidden">
                                <span className="text-[40px] rounded-full overflow-hidden border border-border">
                                  <Flag code={selectedNumberData?.country} squared />
                                </span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="text-3xl font-bold text-gray-950">
                                {selectedNumberData.phone_number}
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <div className="flex space-x-1">
                                  {renderCapabilities(selectedNumberData.capabilities)}
                                </div>
                                <span className="text-sm text-gray-500">
                                  {[
                                    selectedNumberData.locality,
                                    selectedNumberData.region,
                                    selectedNumberData.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ") || "Unknown Location"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="mt-4 space-y-2">
                            <div className="flex items-center space-x-3">
                              <div className="w-6 h-6 rounded flex items-center justify-center mt-0.5">
                                <Image
                                  src="/images/icons/untitledui/number-inventory/dollar-phonepanel.svg"
                                  alt="Dollar icon"
                                  className="w-5 h-5"
                                />
                              </div>
                              <span className="text-sm text-gray-600">
                                This number is included with the user seat
                              </span>
                            </div>

                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5">
                                <Image
                                  src="/images/icons/untitledui/number-inventory/massage-phonepanel.svg"
                                  alt="Message icon"
                                  className="w-5 h-5"
                                />
                              </div>
                              <span className="text-sm text-gray-500">
                                To use SMS & MMS services, You must{" "}
                                <button className="text-blue-600 hover:underline font-medium">
                                  register this number in 10DLC
                                </button>
                              </span>
                            </div>
                          </div>

                          {/* Choose different number button */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <button
                              onClick={() => {
                                setSelectedNumberData(null);
                                setSelectedNumber("");
                              }}
                              className="w-full flex justify-center items-center px-3 py-1.5 border border-[#D6BBFB] rounded-md text-sm font-semibold text-[#6941C6] shadow-sm"
                            >
                              <Edit2 className="h-4 w-4 mr-1.5" />
                              Choose a different number
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Choose number to assign */}
                      <div className="space-y-4">
                        <div>
                          <div className="flex-1">
                            <Label className="text-base font-medium text-gray-[#414651]">
                              Choose number to assign
                            </Label>

                            <div className="mt-[6px] space-y-3">
                              <Select
                                value={selectedNumber}
                                onValueChange={(value) => {
                                  // Prevent selection of placeholder values
                                  if (value !== "loading" && value !== "no-numbers") {
                                    setSelectedNumber(value);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full h-11 border-gray-300">
                                  <div className="flex items-center space-x-2">
                                    <Search className="h-4 w-4 text-gray-400" />
                                    <SelectValue
                                      placeholder={
                                        getPhoneNumbersFn.isLoading
                                          ? "Loading numbers..."
                                          : "Select numbers to assign"
                                      }
                                    />
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {getPhoneNumbersFn.isLoading ? (
                                    <SelectItem value="loading" disabled>
                                      Loading phone numbers...
                                    </SelectItem>
                                  ) : availableNumbers.length === 0 ? (
                                    <SelectItem value="no-numbers" disabled>
                                      No phone numbers available
                                    </SelectItem>
                                  ) : (
                                    availableNumbers.map((number) => (
                                      <SelectItem key={number.id} value={number.id}>
                                        <div className="flex flex-col">
                                          <span className="font-medium">{number.number}</span>
                                          <span className="text-xs text-gray-500">{number.location}</span>
                                        </div>
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Default view - Purchase Number Option */}
                      <div className="space-y-4">
                        <div>
                          <div className="flex-1">
                            {/* Illustration */}
                            <div className="mt-4 mb-6 flex justify-center">
                              <Image
                                src="/images/icons/untitledui/number-inventory/communication.svg"
                                alt="add-dialer-user"
                                className="no-invert"
                              />
                            </div>

                            {/* Enhanced Purchase Button */}
                            <div className="mt-4">
                              <Button
                                onClick={handleOpenPurchasePanel}
                                disabled={!selectedUser}
                                variant="outline"
                                className={`w-full h-11 border-2 font-medium transition-all duration-200 shadow-sm ${
                                  selectedUser
                                    ? "text-[#7F56D9] border-[#D6BBFB] bg-white hover:bg-[#F4F3FF]"
                                    : "text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed opacity-50"
                                }`}
                              >
                                <ShoppingCart className="h-4 w-4 mr-2 text-[#6941C6] font-semibold" />
                                <span className="text-[#6941C6] font-semibold">
                                  Choose a number from 100+ available countries (Included with user seat)
                                </span>
                              </Button>
                            </div>

                            <div className="text-center text-gray-400 text-sm mt-4 mb-4">OR</div>
                          </div>
                        </div>
                      </div>

                      {/* Assign Number Option - Show as alternative when no number selected from purchase panel */}
                      <div className="space-y-4">
                        <div>
                          <div className="flex-1">
                            <Label className="text-sm font-medium text-gray-[#414651]">
                              Assign an existing number
                            </Label>

                            <div className="mt-[6px] space-y-3">
                              <Select
                                value={selectedNumber}
                                onValueChange={(value) => {
                                  // Prevent selection of placeholder values
                                  if (value !== "loading" && value !== "no-numbers") {
                                    setSelectedNumber(value);
                                    // Clear purchased number data when selecting from dropdown
                                    setSelectedNumberData(null);
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full h-12 border-gray-300">
                                  <div className="flex items-center space-x-2">
                                    <Search className="h-4 w-4 text-gray-400" />
                                    <SelectValue
                                      placeholder={
                                        getPhoneNumbersFn.isLoading
                                          ? "Loading numbers..."
                                          : "Select numbers to assign"
                                      }
                                    />
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {getPhoneNumbersFn.isLoading ? (
                                    <SelectItem value="loading" disabled>
                                      Loading phone numbers...
                                    </SelectItem>
                                  ) : availableNumbers.length === 0 ? (
                                    <SelectItem value="no-numbers" disabled>
                                      No phone numbers available
                                    </SelectItem>
                                  ) : (
                                    availableNumbers.map((number) => (
                                      <SelectItem key={number.id} value={number.id}>
                                        <div className="flex items-start">
                                          <span className="text-[24px] inline-flex items-center justify-center mr-2 mt-1 rounded-full overflow-hidden border border-border">
                                            <Flag code={number?.countryCode} squared />
                                          </span>

                                          <div className="">
                                            <div className="font-semibold text-[#101828] dark:text-white text-sm capitalize">
                                              {number.number}{" "}
                                              <span className="font-normal text-xs">({number.label})</span>
                                            </div>
                                            <div className="text-xs text-[#535862] dark:text-gray-400 font-normal mt-1 text-left">
                                              {number.location}
                                            </div>
                                          </div>
                                        </div>
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase Number Panel */}
      <PurchaseNumberPanel />
    </div>
  );
}
